import { ref } from 'vue';
import { defineStore } from 'pinia';
import ApiService from '@/services/ApiService';
import JwtService from '@/services/JwtService';
import type { UserLogged } from '@/types/user';
import { useRouter } from 'vue-router';
import * as Sentry from '@sentry/vue';
import { identifySessionUser } from '@/plugins/openreplay';
const base_api = 'api/auth/v1/';

export const useAuthStore = defineStore('auth', () => {
  const errors = ref('');
  const router = useRouter();
  const user = ref<UserLogged>({} as UserLogged);
  const isAuthenticated = ref(!!JwtService.getToken());

  const setAuth = (authUser: UserLogged): void => {
    isAuthenticated.value = true;
    user.value = authUser;
    JwtService.saveToken(user.value.token);
    // Dashboard widgets fetch concurrently (and some poll on an interval, see
    // usePolling.ts), so a single expired session can trigger several near-simultaneous
    // 401s. ApiService latches a guard after the first one so it only shows the
    // "session expired" prompt once; that guard must be released on a fresh login or
    // it would silently suppress the alert for the rest of the session.
    ApiService.resetSessionExpiredGuard();

    Sentry.setUser({
      id: user.value.id,
      email: user.value.email,
      username: user.value.name,
      ip_address: '{{auto}}',
    });

    identifySessionUser({
      id: user.value.id,
      name: user.value.name,
      email: user.value.email,
    });
  };

  const purgeAuth = (): void => {
    isAuthenticated.value = false;
    user.value = {} as UserLogged;
    JwtService.destroyToken();
    JwtService.destroyPermissions();

    // Clear user context in Sentry. The OpenReplay session itself keeps
    // recording (see initSessionRecording() in main.ts) — logging out doesn't
    // stop it, since post-logout activity is worth capturing too.
    Sentry.setUser(null);
  };

  const logout = async (): Promise<void> => {
    if (JwtService.getToken()) {

      try {
        await ApiService.request({
          method: 'POST',
          url: base_api + 'logout'
        });
      } catch (e: any) {
        console.log('token is invalid. already logged out from serverside');
      }

    }
    purgeAuth();
    router.push('/sign-in');
  };

  const setLoginData = (loginData: any): void => {
    const user = loginData?.user;
    const token = loginData?.token;
    setAuth({ ...user, token });
  };

  const setPermissions = (permissions: string[]): void => {
    JwtService.savePermissions(permissions);
  };

  return {
    errors,
    user,
    isAuthenticated,
    logout,
    setLoginData,
    setPermissions
  };
});
